import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User, LoginCredentials, RegisterData } from '../../types/auth';
import api, { apiClient, refreshAccessTokenOnce } from '../../api/apiClient';
import { API_CONFIG } from '../../config/api';
import { storeTokens, storeCsrfToken, clearTokens, clearLegacyLocalStorageTokens, getToken, getCsrfToken } from '../../utils/sessionManager';
import { isConnectionAuthError, toAuthErrorMessage } from '../../utils/authError';

// Access token is memory-only; wipe legacy localStorage tokens (XSS surface).
clearLegacyLocalStorageTokens();

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  status: 'idle'
};

// Login
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { dispatch, rejectWithValue }) => {
    try {
      console.log('Login attempt with:', credentials.email);
      const response = await api.auth.login({
        email: credentials.email,
        password: credentials.password,
        turnstileToken: credentials.turnstileToken || '',
      });
      console.log('Login response received');
      
      // The response structure should contain token directly (not nested in data.data)
      if (!response.data || !response.data.token) {
        console.error('Login response missing token:', response.data);
        return rejectWithValue('No authentication token received');
      }
      
      const token = response.data.token;
      const expiresIn = response.data.expiresIn || 3600; // Default to 1 hour if not provided
      
      // Access token in memory; refresh cookie set by server (withCredentials)
      storeTokens(token, undefined, expiresIn);
      storeCsrfToken(response.data.csrfToken);
      
      // After login, fetch the user profile
      try {
        const profileResponse = await api.user.getProfile();
        if (!profileResponse.data || !profileResponse.data.data) {
          throw new Error('Invalid profile response structure');
        }
        
        const userData = profileResponse.data.data;
        // Normalize user data to ensure all fields are properly set
        const normalizedUser: User = {
          ...userData,
          id: userData._id || userData.id,
          userType: userData.userType || userData.role,
          role: userData.role || userData.userType,
          // Preserve adminRole if it exists
          adminRole: userData.adminRole,
        };
        
        return {
          token: token,
          refreshToken: null,
          user: normalizedUser
        };
      } catch (profileErr: any) {
        console.error('Profile fetch error after login:', profileErr);
        return {
          token: token,
          refreshToken: null,
          // Return basic user info if available in login response
          user: response.data.user || null
        };
      }
    } catch (err: any) {
      console.error('Login error:', err);
      const msg = err.data?.message || err.message || 'Login failed.';
      return rejectWithValue(msg);
    }
  }
);

// Register
export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterData, { dispatch, rejectWithValue }) => {
    try {
      const extra = localStorage.getItem('registrationExtra');
      let data = { ...userData };
      if (extra) {
        const parsed = JSON.parse(extra);
        if (parsed.location) data.location = parsed.location;
        if (parsed.stylistProfile) data.stylistProfile = parsed.stylistProfile;
        if (parsed.paymentMethodId) data.paymentMethodId = parsed.paymentMethodId;
        if (parsed.stripeCustomerId) data.stripeCustomerId = parsed.stripeCustomerId;
        localStorage.removeItem('registrationExtra');
      }
      
      console.log('Register attempt with:', data.email);
      const response = await api.auth.register(data);
      console.log('Register response received');
      
      // The response structure should contain token directly
      if (!response.data || !response.data.token) {
        console.error('Register response missing token:', response.data);
        return rejectWithValue('No authentication token received');
      }
      
      const token = response.data.token;
      const expiresIn = response.data.expiresIn || 3600;
      storeTokens(token, undefined, expiresIn);
      storeCsrfToken(response.data.csrfToken);
      
      // After registration, fetch the user profile
      try {
        const profileResponse = await api.user.getProfile();
        if (!profileResponse.data || !profileResponse.data.data) {
          throw new Error('Invalid profile response structure');
        }
        
        const userData = profileResponse.data.data;
        // Normalize user data to ensure all fields are properly set
        const normalizedUser: User = {
          ...userData,
          id: userData._id || userData.id,
          userType: userData.userType || userData.role,
          role: userData.role || userData.userType,
          // Preserve adminRole if it exists
          adminRole: userData.adminRole,
        };
        
        return {
          token: token,
          refreshToken: null,
          user: normalizedUser
        };
      } catch (profileErr: any) {
        console.error('Profile fetch error after registration:', profileErr);
        return {
          token: token,
          refreshToken: null,
          // Return basic user info if available in registration response
          user: response.data.user || null
        };
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      const msg = err.data?.message || err.message || 'Registration failed.';
      return rejectWithValue(msg);
    }
  }
);

// Refresh token thunk
export const refreshAuthToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    const csrfBeforeRefresh = getCsrfToken();
    try {
      // Cookie-based refresh (HttpOnly); CSRF header attached by apiClient
      const response = await apiClient.post(API_CONFIG.REFRESH_ENDPOINT, {});
      const { token, expiresIn, csrfToken } = response.data;
      storeTokens(token, undefined, expiresIn || 3600);
      storeCsrfToken(csrfToken);
      return { token, refreshToken: null as string | null };
    } catch (err: any) {
      // Another tab may have successfully rotated the cookie and CSRF value
      // while this request was failing. Never erase that newer generation.
      const currentCsrf = getCsrfToken();
      clearTokens({
        preserveCsrf: !!currentCsrf && currentCsrf !== csrfBeforeRefresh,
      });
      return rejectWithValue(err.message || 'Session expired');
    }
  }
);

// Logout thunk
export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await apiClient.post('/auth/logout');
      clearTokens();
      return null;
    } catch (err: any) {
      clearTokens();
      return rejectWithValue(err.message || 'Logout failed');
    }
  }
);

// Request deduplication - prevent multiple simultaneous calls
let pendingProfileRequest: Promise<any> | null = null;

// Fetch current user
export const getCurrentUser = createAsyncThunk(
  'auth/getCurrentUser',
  async (_, { rejectWithValue }) => {
    // If there's already a pending request, return it instead of making a new one
    if (pendingProfileRequest) {
      try {
        return await pendingProfileRequest;
      } catch (err) {
        // Clear pending request on error so next call can retry
        pendingProfileRequest = null;
        throw err;
      }
    }

    // Create new request
    pendingProfileRequest = (async () => {
      try {
        // Restore access token from refresh cookie when memory is empty (page reload)
        if (!getToken()) {
          try {
            await refreshAccessTokenOnce();
          } catch {
            // no session cookie — profile fetch will 401
          }
        }
        // Use profile endpoint
        const response = await api.user.getProfile();
        const userData = response.data.data;
        
        // Normalize user data to ensure all fields are properly set
        const normalizedUser: User = {
          ...userData,
          id: userData._id || userData.id,
          userType: userData.userType || userData.role,
          role: userData.role || userData.userType,
          // Preserve adminRole if it exists
          adminRole: userData.adminRole,
        };
        
        return normalizedUser;
      } catch (err: any) {
        // apiClient unwraps Axios errors into `{ status, message, code }` with
        // no `.response`. Missing `.response` is therefore not a connection
        // failure — a 401 looks the same shape as a refused connection.
        if (isConnectionAuthError(err) || err.status === 401) {
          clearTokens();
        }
        throw err;
      } finally {
        // Clear pending request after completion
        pendingProfileRequest = null;
      }
    })();

    try {
      return await pendingProfileRequest;
    } catch (err: any) {
      if (isConnectionAuthError(err)) {
        return rejectWithValue({
          message: err.message || 'Backend server is not available',
          code: err.code || 'ERR_CONNECTION_REFUSED',
          status: err.status ?? 0,
        });
      }
      // Expired/missing session: send the user to login without a banner.
      if (err.status === 401) {
        return rejectWithValue(null);
      }
      return rejectWithValue(err.message || 'Failed to fetch profile');
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => { state.error = null; },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    updateUserPreferences: (state, action: PayloadAction<NonNullable<User['preferences']>>) => {
      if (!state.user || !action.payload) return;
      state.user.preferences = {
        ...state.user.preferences,
        ...action.payload,
        notifications: {
          ...state.user.preferences?.notifications,
          ...action.payload.notifications,
        },
        display: {
          ...state.user.preferences?.display,
          ...action.payload.display,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (s) => { s.isLoading = true; s.error = null; s.status = 'loading'; })
      .addCase(login.fulfilled, (s, a: PayloadAction<{user:any;token:string;refreshToken:string|null}>) => {
        console.log('Login fulfilled with payload:', a.payload);
        s.isLoading = false; 
        s.isAuthenticated = true; 
        s.user = a.payload.user; 
        s.token = a.payload.token; 
        s.refreshToken = a.payload.refreshToken; 
        s.status = 'succeeded';
      })
      .addCase(login.rejected, (s, a) => { 
        console.error('Login rejected:', a.payload);
        s.isLoading = false; 
        s.error = toAuthErrorMessage(a.payload); 
        s.status = 'failed'; 
      })

      .addCase(register.pending, (s) => { s.isLoading = true; s.error = null; s.status = 'loading'; })
      .addCase(register.fulfilled, (s, a: PayloadAction<{user:any;token:string;refreshToken:string|null}>) => {
        console.log('Register fulfilled with payload:', a.payload);
        s.isLoading = false; 
        s.isAuthenticated = true; 
        s.user = a.payload.user; 
        s.token = a.payload.token; 
        s.refreshToken = a.payload.refreshToken; 
        s.status = 'succeeded';
      })
      .addCase(register.rejected, (s, a) => { 
        console.error('Register rejected:', a.payload);
        s.isLoading = false; 
        s.error = toAuthErrorMessage(a.payload); 
        s.status = 'failed'; 
      })

      .addCase(refreshAuthToken.fulfilled, (s, a: PayloadAction<{token:string;refreshToken:string}>) => {
        s.token=a.payload.token; s.refreshToken=a.payload.refreshToken;
      })
      .addCase(refreshAuthToken.rejected, (s) => {
        s.isAuthenticated=false; s.user=null; s.token=null; s.refreshToken=null; s.status='idle';
      })

      .addCase(logout.pending, (s) => { 
        s.isLoading = true; 
        s.status = 'loading'; 
      })
      .addCase(logout.fulfilled, (s) => {
        s.isAuthenticated = false; 
        s.user = null; 
        s.token = null; 
        s.refreshToken = null; 
        s.isLoading = false; 
        s.status = 'idle'; 
        s.error = null;
      })
      .addCase(logout.rejected, (s, a) => {
        // Even if logout API call fails, clear local state since tokens are removed
        // Don't set error to prevent retry loops
        s.isAuthenticated = false; 
        s.user = null; 
        s.token = null; 
        s.refreshToken = null; 
        s.isLoading = false; 
        s.status = 'idle';
        s.error = null; // Clear error to prevent retry loops
      })

      .addCase(getCurrentUser.pending, (s) => {
        s.error = null;
        // Keep an existing session visible; dashboard mount refreshes profile
        // in the background and must not flip the app into a full-page loader.
        if (!s.user) {
          s.isLoading = true;
          s.status = 'loading';
        }
      })
      .addCase(getCurrentUser.fulfilled, (s, a: PayloadAction<User>) => {
        s.isLoading=false; s.isAuthenticated=true; s.user=a.payload; s.status='succeeded';
        s.token = getToken();
      })
      .addCase(getCurrentUser.rejected, (s, a) => {
        s.isLoading=false; 
        s.error=toAuthErrorMessage(a.payload); 
        s.status='failed';
        // Only clear auth state if memory access token was cleared (401 / no session)
        if (!getToken()) {
          s.isAuthenticated=false; 
          s.user=null; 
          s.token=null;
          s.refreshToken=null;
        }
      });
  }
});

export const { clearError, updateUser, updateUserPreferences } = authSlice.actions;
export default authSlice.reducer;
